import { ExclamationTriangleIcon, ExternalLinkIcon } from "@radix-ui/react-icons";
import { Box, Button, Callout, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";
import type { BillingPortalFlow } from "../../../../utils/billing-shared";
import { useUsage } from "../../contexts/UsageContext";
import { useBillingPortal } from "../../hooks/useBillingPortal";
import { trackEvent } from "../../utils/posthogConfig";
import { formatPeriodEnd } from "../../utils/usageMonths";
import { CARD_NO_PADDING } from "../dashboard/tokens";
import type { BillingAccessSummary } from "./SubscriptionCard";

type BillingAction = "payment_method" | "cancel" | "resume" | "manage";

const BUTTON_TONES = {
    neutral: {
        variant: "surface",
        color: "gray",
    },
    primary: {
        variant: "solid",
    },
    danger: {
        variant: "ghost",
        color: "red",
    },
} as const;

type BillingRow = {
    action: BillingAction;
    /** Stripe screen to open; without one the portal home opens. */
    flow?: BillingPortalFlow;
    title: string;
    description: string;
    label: string;
    /** Full action name for screen readers, since the visible label can be a single verb. */
    accessibleLabel: string;
    tone?: keyof typeof BUTTON_TONES;
};

/**
 * Billing tasks for anyone with Pro access or billing history, one row per task. Each opens the
 * Stripe portal in a new tab; payment method and cancel go straight to their screen.
 */
export default function BillingManageCard({ subscription }: { subscription: BillingAccessSummary | null }) {
    const { hasInvoices, loading } = useUsage();
    const { opening, error, openPortal } = useBillingPortal();
    // Only the clicked button shows the opening state.
    const [clickedAction, setClickedAction] = useState<BillingAction | null>(null);
    const hasPro = subscription?.hasProAccess === true;

    if (loading || !(hasInvoices || hasPro)) {
        return null;
    }

    // Stripe states where the last charge failed and Pro is at risk.
    const paymentProblem = hasPro && (subscription.status === "past_due" || subscription.status === "unpaid");

    const rows: BillingRow[] = [];
    if (hasPro) {
        rows.push({
            action: "payment_method",
            flow: "payment_method_update",
            title: "Payment method",
            description: "The card we charge for Pro.",
            label: "Update",
            accessibleLabel: "Update payment method",
            tone: paymentProblem ? "primary" : undefined,
        });
    }
    rows.push({
        action: "manage",
        title: "Invoices and billing",
        description: "Invoices, receipts and the company details on your invoices.",
        label: "Manage",
        accessibleLabel: "Manage invoices and billing",
    });
    // Subscription goes last so Cancel isn't clicked by habit.
    if (hasPro) {
        if (subscription.cancelable) {
            rows.push({
                action: "cancel",
                flow: "subscription_cancel",
                title: "Subscription",
                description: "You keep Pro until the end of the period you paid for.",
                label: "Cancel subscription",
                accessibleLabel: "Cancel subscription",
                tone: "danger",
            });
        } else if (subscription.resumable) {
            // Stripe has no resume screen to link to; the portal home offers it.
            rows.push({
                action: "resume",
                title: "Subscription",
                description: subscription.currentPeriodEnd
                    ? `Pro won't renew after ${formatPeriodEnd(subscription.currentPeriodEnd)}. Resume to keep it.`
                    : "Pro won't renew. Resume to keep it.",
                label: "Resume subscription",
                accessibleLabel: "Resume subscription",
                tone: "primary",
            });
        }
    }

    const open = ({ action, flow }: BillingRow) => {
        void trackEvent("billing_action_clicked", {
            action,
            surface: "billing_page",
            subscription_status: subscription?.status ?? "unknown",
        });
        setClickedAction(action);
        void openPortal(flow);
    };

    return (
        <Card size="3" mt="5" style={CARD_NO_PADDING}>
            <Box px="5" pt="4" pb={paymentProblem ? "0" : "4"}>
                <Heading size="3">Billing</Heading>
            </Box>
            {paymentProblem && (
                <Box px="5" py="3">
                    <Callout.Root color="amber" size="1" role="alert">
                        <Callout.Icon>
                            <ExclamationTriangleIcon />
                        </Callout.Icon>
                        <Callout.Text>Your last payment didn't go through. Update your payment method to keep Pro.</Callout.Text>
                    </Callout.Root>
                </Box>
            )}
            {rows.map((row) => (
                <Flex key={row.action} align="center" justify="between" gap="4" wrap="wrap" className="dc-row">
                    <Box
                        style={{
                            flex: "1 1 260px",
                            minWidth: 0,
                        }}
                    >
                        <Text as="div" size="2" weight="medium">
                            {row.title}
                        </Text>
                        <Text as="div" size="2" color="gray">
                            {row.description}
                        </Text>
                    </Box>
                    <Button
                        type="button"
                        {...BUTTON_TONES[row.tone ?? "neutral"]}
                        disabled={opening}
                        onClick={() => open(row)}
                        aria-label={`${row.accessibleLabel}, opens Stripe in a new tab`}
                        // Radix ghost buttons have a negative margin; reset it so the button lines up with the others.
                        style={{
                            flex: "none",
                            margin: row.tone === "danger" ? 0 : undefined,
                        }}
                    >
                        {opening && clickedAction === row.action ? "Opening…" : row.label}
                        <ExternalLinkIcon aria-hidden="true" />
                    </Button>
                </Flex>
            ))}
            <Box
                px="5"
                py="3"
                style={{
                    borderTop: "1px solid var(--dc-card-border)",
                }}
            >
                {error ? (
                    <Text as="p" size="2" color="red" role="alert">
                        {error}
                    </Text>
                ) : (
                    <Text as="p" size="1" color="gray">
                        These open Stripe, our payment provider, in a new tab.
                    </Text>
                )}
            </Box>
        </Card>
    );
}
